/** Same-origin service worker registration + install prompt plumbing. */

export type ServiceWorkerState =
  | { status: 'unsupported' }
  | { status: 'insecure-context' }
  | { status: 'registered'; scope: string }
  | { status: 'error'; message: string };

let lastState: ServiceWorkerState | null = null;
const listeners = new Set<(state: ServiceWorkerState) => void>();

function publish(state: ServiceWorkerState) {
  lastState = state;
  listeners.forEach((listener) => listener(state));
}

export function onServiceWorkerState(listener: (state: ServiceWorkerState) => void): () => void {
  listeners.add(listener);
  if (lastState) listener(lastState);
  return () => listeners.delete(listener);
}

export async function registerServiceWorker(): Promise<ServiceWorkerState> {
  if (!('serviceWorker' in navigator)) {
    const state: ServiceWorkerState = { status: 'unsupported' };
    publish(state);
    return state;
  }
  if (!window.isSecureContext) {
    const state: ServiceWorkerState = { status: 'insecure-context' };
    publish(state);
    return state;
  }
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    const state: ServiceWorkerState = { status: 'registered', scope: registration.scope };
    publish(state);
    return state;
  } catch (error) {
    const state: ServiceWorkerState = {
      status: 'error',
      message: error instanceof Error ? error.message : String(error),
    };
    publish(state);
    return state;
  }
}

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
});

export function isInstallPromptAvailable(): boolean {
  return deferredPrompt !== null;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice.outcome;
}
